import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { thumb } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Play, Clock, Lock, Sparkles, Check, ArrowRight } from "lucide-react";

export default function Tutorials() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [member, setMember] = useState(null);

  useEffect(() => { api.get("/tutorials").then(({ data }) => setItems(data)); }, []);
  useEffect(() => {
    if (!user || user === false) { setMember(null); return; }
    api.get("/membership/me").then(({ data }) => setMember(data)).catch(() => setMember(null));
  }, [user]);

  const isMember = member?.active;

  return (
    <div data-testid="tutorials-page" className="pt-32 pb-24 fade-in bg-[#F2E9D8]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-12 bg-[#121212]" />
          <span className="text-xs tracking-[0.3em] uppercase font-bold">Members-only digital library</span>
        </div>
        <h1 data-testid="vault-title" className="font-serif font-black text-5xl sm:text-7xl text-[#121212] max-w-4xl leading-[0.95]">
          The Cake Society <span className="lime-swipe italic">Vault.</span>
        </h1>
        <p className="mt-8 text-lg text-[#121212]/80 max-w-2xl leading-relaxed">
          A growing library of decorating tutorials, techniques, and creative projects —
          streamed anytime, unlocked the moment you join the club. Learn at your own pace,
          alongside a community that loves to create.
        </p>

        {/* Membership access banner */}
        {isMember ? (
          <div data-testid="vault-member-banner" className="mt-10 inline-flex items-center gap-3 bg-[#C6FF33] border-2 border-[#121212] px-6 py-3">
            <Sparkles size={18} className="text-[#121212]" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold">You're in — unlimited Vault access is active</span>
          </div>
        ) : (
          <div data-testid="vault-join-banner" className="mt-10 bg-[#121212] text-[#F2E9D8] p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Unlock the whole library</p>
              <p className="mt-2 text-lg max-w-xl">
                The Vault is included with The Cake Society Membership — unlimited access to every
                tutorial while you're a member, plus studio time, savings and early access.
              </p>
            </div>
            <Link
              to="/membership"
              data-testid="vault-join-cta"
              className="shrink-0 inline-flex items-center gap-3 px-8 py-4 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] text-xs tracking-[0.25em] uppercase font-bold transition-all"
            >
              Join the club <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {/* Library grid */}
        {items.length === 0 ? (
          <div data-testid="tutorials-empty" className="mt-16 border-2 border-dashed border-[#121212]/25 bg-white/60 py-20 px-6 text-center">
            <div className="w-16 h-16 mx-auto mb-5 bg-[#121212] flex items-center justify-center">
              <Lock className="text-[#C6FF33]" size={24} />
            </div>
            <h3 className="font-serif font-black text-3xl text-[#121212]">New tutorials are on the way</h3>
            <p className="mt-3 text-[#121212]/70 max-w-md mx-auto">
              We're filming fresh decorating tutorials for the Vault right now. Become a member to be the first to watch when they drop.
            </p>
            <Link to="/membership" data-testid="tutorials-empty-cta" className="mt-7 inline-flex items-center gap-2 px-7 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all">
              Explore membership
            </Link>
          </div>
        ) : (
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((t) => {
            const accessType = t.access_type || "vault";
            const unlocked = isMember && accessType === "vault";
            return (
              <Link
                key={t.id}
                to={`/tutorials/${t.id}`}
                data-testid={`tutorial-card-${t.id}`}
                className="group bg-white border-2 border-[#121212] hover:-translate-y-1 transition-all flex flex-col"
              >
                <div className="aspect-video overflow-hidden relative">
                  <img src={thumb(t.image_url, 600)} alt={t.title} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${unlocked ? "" : "grayscale-[35%]"}`} />
                  <div className="absolute inset-0 bg-stone-900/30 flex items-center justify-center">
                    <div className="w-14 h-14 bg-white/90 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                      {unlocked ? <Play className="text-[#121212] ml-1" size={22} /> : <Lock className="text-[#121212]" size={20} />}
                    </div>
                  </div>
                  <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur text-xs tracking-wider uppercase text-[#121212] font-semibold">
                    {t.level}
                  </span>
                  {accessType === "premium" && (
                    <span className="absolute top-3 right-3 px-3 py-1 bg-[#E0218A] text-white text-xs tracking-wider uppercase font-bold">Premium</span>
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-serif font-black text-2xl text-[#121212] leading-tight">{t.title}</h3>
                  <p className="mt-2 text-[#121212]/70 text-sm line-clamp-2 flex-1">{t.description}</p>
                  <div className="mt-5 pt-5 border-t border-[#121212]/10 flex justify-between items-center">
                    <span className="text-[#121212]/55 text-sm inline-flex items-center gap-1.5"><Clock size={14} /> {t.duration}</span>
                    {accessType === "premium" ? (
                      <span className="font-serif font-black text-2xl text-[#121212]">${t.price}</span>
                    ) : unlocked ? (
                      <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212] inline-flex items-center gap-1.5"><Check size={14} /> Included</span>
                    ) : (
                      <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/50 inline-flex items-center gap-1.5"><Lock size={12} /> Members</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
          </div>
        )}
      </div>
    </div>
  );
}
